import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import PhoneMockup from "@/components/site/PhoneMockup";
import AppStoreBadge from "@/components/site/AppStoreBadge";
import Reveal from "@/components/site/Reveal";
import FloatingBanners from "@/components/site/FloatingBanners";
import { getIcon } from "@/lib/icons";
import { useContent } from "@/contexts/ContentContext";
import { useAppPages } from "@/contexts/AppPagesContext";

export const FeaturedApp = ({ content }) => {
  const c = content || {};
  const { loading, hasCached } = useContent();
  const { findBySlug } = useAppPages();
  const isStillLoading = loading && !hasCached;
  const pillars = c.pillars || [];
  const checks = c.checks || [];
  const cleverCaninePage = findBySlug("clever-canine");

  return (
    <section
      id="apps"
      data-testid="featured-app-section"
      className="relative pf-section overflow-hidden"
    >
      <div className="pointer-events-none absolute -right-32 top-10 h-[420px] w-[420px] pf-hero-blob" aria-hidden="true" />

      <div className="pf-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1 relative" data-testid="featured-phone-wrap">
            <Reveal>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-[#FFEEE6]/60 blur-2xl" aria-hidden="true" />
                <FloatingBanners
                  banners={c.floating_banners}
                  testIdPrefix="featured-floating-banner"
                />
                <PhoneMockup variant="featured" imageId={c.phone_screen_image_id} loading={isStillLoading} testId="featured-phone-mockup" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal>
              <div className="flex items-center gap-2">
                <span className="pf-mono text-[11px] uppercase tracking-wider text-[#D94A14]">
                  {c.eyebrow}
                </span>
                <span className="h-px w-10 bg-[hsl(var(--border))]" />
                <Badge className="rounded-full bg-[#FFEEE6] text-[#D94A14] hover:bg-[#FFEEE6] border-0 px-2.5 py-0.5 text-[11px] font-semibold">
                  {c.badge_label}
                </Badge>
              </div>
            </Reveal>

            <Reveal delay={0.06}>
              <h2
                data-testid="featured-title"
                className="mt-4 pf-display text-[34px] sm:text-[44px] leading-tight tracking-tight text-[hsl(var(--foreground))]"
              >
                {c.title_line1}{" "}
                <span className="text-[#F35A1F]">{c.title_line2}</span>
              </h2>
            </Reveal>

            <Reveal delay={0.12}>
              <p className="mt-4 max-w-xl text-base sm:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
                {c.description}
              </p>
            </Reveal>

            <Reveal delay={0.18}>
              <ul
                data-testid="featured-pillars"
                className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3"
              >
                {pillars.map((p, i) => {
                  const Icon = getIcon(p.icon);
                  const slug = (p.title || `pillar-${i}`).toLowerCase().replace(/\s+/g, "-");
                  return (
                    <li
                      key={`${slug}-${i}`}
                      data-testid={`featured-pillar-${slug}`}
                      className="rounded-2xl border border-[hsl(var(--border))] bg-white p-4 pf-shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:pf-shadow-md"
                    >
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFEEE6] text-[#D94A14]">
                        <Icon className="h-4 w-4" />
                      </span>
                      <p className="mt-3 pf-display text-[15px] text-[hsl(var(--foreground))]">
                        {p.title}
                      </p>
                      <p className="mt-1 text-[13px] leading-relaxed text-[hsl(var(--muted-foreground))]">
                        {p.desc}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <AppStoreBadge
                  href={c.app_store_url || "#"}
                  testId="featured-download-button"
                />
                {cleverCaninePage ? (
                  <Link
                    to={`/apps/${cleverCaninePage.slug}`}
                    data-testid="featured-learn-more"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D94A14] hover:text-[#F35A1F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 rounded-md"
                  >
                    See full feature tour
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <a
                    href={c.learn_more_url || "#"}
                    data-testid="featured-learn-more"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D94A14] hover:text-[#F35A1F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 rounded-md"
                  >
                    See full feature tour
                    <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                {checks.map((t, i) => (
                  <li key={`${t}-${i}`} className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-[#1F9D6A]" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedApp;
